import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  arrowStyle as arrowStyleFor,
  computePlacement,
  fixedStyle,
  type PlacedRect,
  type Placement,
} from '../components/shared/index.js';

export interface UseFloatingOptions {
  /** Whether the floating layer should currently be positioned. Turn off to
   *  stop tracking scroll/resize and drop the stale rect. */
  readonly open: boolean;
  /** Desired placement; falls back to the opposite side if `flip` is on and
   *  there is no room. Defaults to `'bottom-start'`. */
  readonly placement?: Placement;
  /** Gap between anchor and floating layer, px. Default 8. */
  readonly gap?: number;
  /** Minimum distance from viewport edges, px. Default 8. */
  readonly padding?: number;
  /** Flip to the opposite side when the preferred side has no room. Default true. */
  readonly flip?: boolean;
  /** Optional virtual anchor rect (viewport coordinates). When supplied it
   *  replaces the DOM anchor element — used by ContextMenu / caret-anchored
   *  floats. Anchor ref is ignored while `virtualAnchor` is defined. */
  readonly virtualAnchor?: { top: number; left: number; width: number; height: number } | null;
  /** When true, the floating panel adopts the anchor's width — used by
   *  Combobox and Select where the dropdown should visually attach to the
   *  trigger. The width is exposed via `width` and applied to floatingStyle.
   *  Default false. */
  readonly matchAnchorWidth?: boolean;
  /** Width of the arrow element in px (used to keep its computed offset
   *  outside the panel's rounded corner). Default 12. */
  readonly arrowSize?: number;
  /** Hide the floating layer when the DOM anchor is fully clipped by a scroll
   *  container or leaves the viewport. Useful for portal-backed controls such
   *  as Select, where the layer should not keep covering unrelated content
   *  after its trigger has scrolled away. Default false. */
  readonly hideWhenDetached?: boolean;
}

export interface UseFloatingResult {
  /** Attach to the element that the floating layer anchors to. */
  readonly setAnchor: (el: HTMLElement | null) => void;
  /** Attach to the floating panel so its size can be measured. */
  readonly setFloating: (el: HTMLElement | null) => void;
  /** The computed rect, or null while we haven't yet measured (first paint,
   *  closed, or anchor missing). */
  readonly rect: PlacedRect | null;
  /** Style to spread onto the floating panel root. `position: fixed` + the
   *  computed top/left, or an off-screen parking spot while measuring. When
   *  `matchAnchorWidth` is set, also includes `width`. */
  readonly floatingStyle: CSSProperties;
  /** Style for the arrow element — `left` (top/bottom side) or `top`
   *  (left/right side). Spread onto the arrow `<span>` so it tracks the
   *  anchor center after viewport clamp. */
  readonly arrowStyle: CSSProperties;
  /** Available height on the chosen side, after gap + padding. Pass to a
   *  `max-height` style on scrollable panel content so long lists shrink
   *  to fit instead of overflowing the viewport. 0 while unmeasured. */
  readonly maxHeight: number;
  /** Available width on the chosen side. */
  readonly maxWidth: number;
  /** Whether the anchor currently intersects the viewport and all clipping
   *  scroll ancestors. Virtual anchors are checked against the viewport only. */
  readonly anchorVisible: boolean;
  /** Imperatively re-measure (e.g. after content size changes). */
  readonly update: () => void;
}

const OFFSCREEN_STYLE: CSSProperties = { position: 'fixed', top: -9999, left: -9999 };
const EMPTY_STYLE: CSSProperties = {};

/** Shared positioning hook for Tooltip / Popover / Menu / HoverCard / Dropdown
 *  / FloatingToolbar — covers the bookkeeping each had inlined: anchor + panel
 *  ref capture, initial layout measurement, tracking scroll + resize while the
 *  layer is open, ResizeObserver on both anchor and panel, and the off-screen
 *  parking style used on the first paint before we know the panel size.
 *
 *  The math itself still lives in `components/shared/position.ts`; this hook
 *  is the "how do I wire it up in React" part. */
export function useFloating(options: UseFloatingOptions): UseFloatingResult {
  const {
    open,
    placement = 'bottom-start',
    gap = 8,
    padding = 8,
    flip = true,
    virtualAnchor,
    matchAnchorWidth = false,
    arrowSize = 12,
    hideWhenDetached = false,
  } = options;

  const anchorRef = useRef<HTMLElement | null>(null);
  const floatingRef = useRef<HTMLElement | null>(null);
  const [rect, setRect] = useState<PlacedRect | null>(null);
  const [anchorVisible, setAnchorVisible] = useState(true);

  // Freeze virtualAnchor by value so callers can pass an inline object
  // literal without causing a re-measurement loop on every render.
  const vaKey = virtualAnchor
    ? `${String(virtualAnchor.top)},${String(virtualAnchor.left)},${String(virtualAnchor.width)},${String(virtualAnchor.height)}`
    : '';

  const update = useCallback((): void => {
    const floating = floatingRef.current;
    if (floating === null) return;
    let a: { top: number; left: number; width: number; height: number } | null = null;
    let anchorEl: HTMLElement | null = null;
    if (virtualAnchor !== undefined && virtualAnchor !== null) {
      a = virtualAnchor;
    } else if (anchorRef.current !== null) {
      anchorEl = anchorRef.current;
      const r = anchorEl.getBoundingClientRect();
      a = { top: r.top, left: r.left, width: r.width, height: r.height };
    }
    if (a === null) return;
    const nextAnchorVisible = anchorIntersectsClip(a, anchorEl);
    setAnchorVisible((prev) => (prev === nextAnchorVisible ? prev : nextAnchorVisible));
    const p = readFloatingSize(floating);
    // When matchAnchorWidth, swap in the anchor width so flip / fit math
    // reflects the size the panel will actually render at.
    const panelSize = matchAnchorWidth
      ? { width: a.width, height: p.height }
      : { width: p.width, height: p.height };
    const next = computePlacement(a, panelSize, {
      placement,
      gap,
      padding,
      flip,
      arrowSize,
    });
    const finalRect: PlacedRect = matchAnchorWidth ? { ...next, width: a.width } : next;
    setRect((prev) => {
      if (
        prev !== null &&
        prev.top === finalRect.top &&
        prev.left === finalRect.left &&
        prev.side === finalRect.side &&
        prev.align === finalRect.align &&
        prev.transformOrigin === finalRect.transformOrigin &&
        prev.arrowOffset === finalRect.arrowOffset &&
        prev.availableHeight === finalRect.availableHeight &&
        prev.availableWidth === finalRect.availableWidth &&
        prev.width === finalRect.width
      ) {
        return prev;
      }
      return finalRect;
    });
    // virtualAnchor is read live; vaKey in deps keeps update stable unless
    // the anchor value actually changes.
  }, [placement, gap, padding, flip, vaKey, matchAnchorWidth, arrowSize]);

  const setAnchor = useCallback((el: HTMLElement | null): void => {
    anchorRef.current = el;
  }, []);

  const setFloating = useCallback(
    (el: HTMLElement | null): void => {
      floatingRef.current = el;
      if (el !== null && open) update();
    },
    [open, update],
  );

  useLayoutEffect(() => {
    if (!open) {
      setRect(null);
      setAnchorVisible(true);
      return;
    }
    update();
  }, [open, update]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (): void => {
      update();
    };
    // capture: true so ancestor scroll containers also trigger a reflow —
    // scroll events don't bubble, but capture-phase listeners on window
    // receive scrolls from descendant scrollable elements. document is also
    // registered because viewport scrolling is dispatched there in some
    // browser runtimes.
    window.addEventListener('scroll', handler, true);
    document.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      document.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, update]);

  // ResizeObserver — re-measure when the anchor's size or the panel's content
  // size changes (e.g. async menu load, locale change wider labels, expand
  // textarea trigger). Without this, a panel whose content grows after open
  // stays positioned for its initial size and visibly overflows.
  useEffect(() => {
    if (!open) return undefined;
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      update();
    });
    if (anchorRef.current !== null) ro.observe(anchorRef.current);
    if (floatingRef.current !== null) ro.observe(floatingRef.current);
    return () => {
      ro.disconnect();
    };
  }, [open, update]);

  const floatingStyle = useMemo<CSSProperties>(() => {
    if (rect === null) return OFFSCREEN_STYLE;
    const base = fixedStyle(rect);
    const visibilityStyle: CSSProperties =
      hideWhenDetached && !anchorVisible ? { visibility: 'hidden', pointerEvents: 'none' } : EMPTY_STYLE;
    if (rect.width !== undefined) {
      return { ...base, ...visibilityStyle, width: `${String(rect.width)}px` };
    }
    return visibilityStyle === EMPTY_STYLE ? base : { ...base, ...visibilityStyle };
  }, [rect, hideWhenDetached, anchorVisible]);

  const arrowStyle = useMemo<CSSProperties>(
    () => (rect !== null ? arrowStyleFor(rect) : EMPTY_STYLE),
    [rect],
  );

  return {
    setAnchor,
    setFloating,
    rect,
    floatingStyle,
    arrowStyle,
    maxHeight: rect?.availableHeight ?? 0,
    maxWidth: rect?.availableWidth ?? 0,
    anchorVisible,
    update,
  };
}

function readFloatingSize(floating: HTMLElement): { width: number; height: number } {
  const rect = floating.getBoundingClientRect();
  return {
    width: maxPositive(rect.width, floating.offsetWidth),
    height: maxPositive(rect.height, floating.offsetHeight),
  };
}

function maxPositive(...values: readonly number[]): number {
  let next = 0;
  for (const value of values) {
    if (Number.isFinite(value) && value > next) next = value;
  }
  return next;
}

interface Edges {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

interface AnchorRect {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

function anchorIntersectsClip(anchor: AnchorRect, anchorEl: HTMLElement | null): boolean {
  const anchorEdges = edgesFromRect(anchor);
  let clip = viewportEdges();
  if (anchorEl !== null) {
    for (let el = anchorEl.parentElement; el !== null; el = el.parentElement) {
      if (el === document.body || el === document.documentElement) continue;
      if (!clipsOverflow(el)) continue;
      clip = intersectEdges(clip, edgesFromDomRect(el.getBoundingClientRect()));
      if (!hasArea(clip)) return false;
    }
  }
  return intersectsOrTouches(anchorEdges, clip);
}

function viewportEdges(): Edges {
  if (typeof window === 'undefined') {
    return {
      top: Number.NEGATIVE_INFINITY,
      right: Number.POSITIVE_INFINITY,
      bottom: Number.POSITIVE_INFINITY,
      left: Number.NEGATIVE_INFINITY,
    };
  }
  return {
    top: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
    left: 0,
  };
}

function clipsOverflow(el: HTMLElement): boolean {
  if (typeof window === 'undefined') return false;
  const style = window.getComputedStyle(el);
  return [style.overflow, style.overflowX, style.overflowY].some((value) => (
    value === 'auto' || value === 'scroll' || value === 'hidden' || value === 'clip'
  ));
}

function edgesFromRect(rect: AnchorRect): Edges {
  return {
    top: rect.top,
    right: rect.left + rect.width,
    bottom: rect.top + rect.height,
    left: rect.left,
  };
}

function edgesFromDomRect(rect: DOMRect): Edges {
  return {
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
    left: rect.left,
  };
}

function intersectEdges(a: Edges, b: Edges): Edges {
  return {
    top: Math.max(a.top, b.top),
    right: Math.min(a.right, b.right),
    bottom: Math.min(a.bottom, b.bottom),
    left: Math.max(a.left, b.left),
  };
}

function hasArea(rect: Edges): boolean {
  return rect.right > rect.left && rect.bottom > rect.top;
}

function intersectsOrTouches(a: Edges, b: Edges): boolean {
  return a.right >= b.left && a.left <= b.right && a.bottom >= b.top && a.top <= b.bottom;
}
